import { defineStore } from 'pinia'

import AuthScope from '~/enums/auth-scope'
import { ProductQuestionStatus } from '~/enums/product-question-status'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { SelectedItem } from '~/types/form-elements'
import type { Page } from '~/types/json-api'
import type { ProductQuestionData } from '~/types/product-questions'

type Filter = {
  productId: string | null
  query: string | null
  selectedProduct: SelectedItem | null
  status: ProductQuestionStatus | null
}

type State = {
  filter: Filter,
  form: {
    answer: string | null
    productId: string | null
    question: string | null
    status: ProductQuestionStatus
  }
  hasUnsavedChanges: boolean
  isDataLoadingError: boolean
  isLoading: boolean
  isProductQuestionDataLoading: boolean
  isProductQuestionsDataLoading: boolean
  page: Page
  productQuestionData: ProductQuestionData | null
  productQuestionsData: ProductQuestionData[]
  totalPages: number | null
  totalProductQuestions: number | null
}

const emptyFilter = { productId: null, query: null, selectedProduct: null, status: null }

export const messages = {
  failedToAnswerProductQuestion: 'Не удалось ответить на вопрос о товаре.',
  failedToCreateProductQuestion: 'Не удалось задать вопрос о товаре.',
  failedToDeleteProductQuestion: 'Не удалось удалить вопрос.',
  failedToGetProductQuestion: 'Не удалось получить вопрос о товаре.',
  failedToGetProductQuestions: 'Не удалось получить вопросы о товарах.',
  failedToUpdateProductQuestion: 'Не удалось сохранить вопрос о товаре.',
  noProductQuestions: 'Нет вопросов о товарах.',
  productQuestionIsCreated: 'Вопрос о товаре создан.',
  productQuestionIsSaved: 'Вопрос о товаре сохранён.',
  productQuestionsAreNotFound: 'Вопросы о товарах не найдены.',
  supplierAnswerIsSaved: 'Ответ отправлен на модерацию.'
}

const initialState = (): State => ({
  filter: { ...emptyFilter },
  form: {
    answer: null,
    productId: null,
    question: null,
    status: ProductQuestionStatus.AwaitingModeration
  },
  hasUnsavedChanges: false,
  isDataLoadingError: false,
  isLoading: false,
  isProductQuestionDataLoading: false,
  isProductQuestionsDataLoading: false,
  page: {
    number: 1,
    size: 20
  },
  productQuestionData: null,
  productQuestionsData: [],
  totalPages: null,
  totalProductQuestions: null
})

export const useProductQuestionsStore = defineStore('productQuestions', {
  state: () => initialState(),
  getters: {
    needToShowFilters (state: State): boolean {
      const { filter, productQuestionsData } = state

      return Boolean(filter.query)
        || filter.query === ''
        || filter.selectedProduct !== null
        || filter.status !== null
        || Boolean(productQuestionsData.length)
    }
  },
  actions: {
    clearFilter (): void {
      this.filter = { ...emptyFilter }
    },
    clearForm (): void {
      this.form.answer = null
      this.form.productId = null
      this.form.question = null
      this.form.status = ProductQuestionStatus.AwaitingModeration
    },
    clearProductQuestion (): void {
      this.productQuestionData = null
    },
    clearProductQuestions (): void {
      this.productQuestionsData = []
    },
    dispose (): void {
      this.clearFilter()
      this.clearProductQuestions()
    },
    productQuestionToForm (): void {
      if (this.productQuestionData) {
        this.clearForm()

        this.form.answer = this.productQuestionData.attributes.answer
        this.form.productId = this.productQuestionData.attributes.productId
        this.form.question = this.productQuestionData.attributes.question
        this.form.status = this.productQuestionData.attributes.statusId
      }
    },
    async answerProductQuestion (productQuestionId: string): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isLoading = true

      try {
        const response = await $apiHelper.productQuestions.answerProductQuestion(
          productQuestionId,
          { answer: this.form.answer },
          AuthScope.Supplier
        ) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.productQuestionData = data

        const questionIndex = this.productQuestionsData.findIndex(
          (x: ProductQuestionData) => x.id === data.attributes.id
        )

        this.productQuestionsData[questionIndex] = data

        $toast.success(messages.supplierAnswerIsSaved)

        this.productQuestionToForm()
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToAnswerProductQuestion)
      }

      this.hasUnsavedChanges = false
      this.isLoading = false
    },
    async createProductQuestion (): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isLoading = true

      try {
        await $apiHelper.productQuestions.createProductQuestion(
          this.form.productId!,
          { question: this.form.question },
          AuthScope.Client
        )

        this.form.question = null

        $toast.success(messages.productQuestionIsCreated)
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToCreateProductQuestion)
      }

      this.hasUnsavedChanges = false
      this.isLoading = false
    },
    async fetchProductQuestion (productQuestionId: string, authScope: AuthScope): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isProductQuestionDataLoading = true
      this.isDataLoadingError = false

      try {
        const response = await $apiHelper.productQuestions.getProductQuestion(productQuestionId, authScope) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.productQuestionData = data

        this.productQuestionToForm()

        this.isProductQuestionDataLoading = false
      } catch (error) {
        this.isDataLoadingError = true

        console.error(error)

        $toast.error(messages.failedToGetProductQuestion)
      }
    },
    async fetchProductQuestions (authScope: AuthScope): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isProductQuestionsDataLoading = true
      this.totalPages = null

      const params: any = helpers.getParams([
        { name: 'filter[query]', value: this.filter.query, condition: this.filter.query },
        { name: 'filter[statusId]', value: this.filter.status, condition: this.filter.status },
        { name: 'page[number]', value: this.page.number, condition: true },
        { name: 'page[size]', value: this.page.size, condition: true }
      ])

      if (this.filter.productId) {
        params['filter[productId]'] = this.filter.productId
      } else if (this.filter.selectedProduct) {
        params['filter[productId]'] = this.filter.selectedProduct.id
      }

      try {
        const response = await $apiHelper.productQuestions.getProductQuestions(params, authScope) as any
        const { data, meta } = JsonApiHelper.denormalizeResponse(response) as any

        this.totalProductQuestions = meta.totalProductQuestions
        this.totalPages = helpers.getTotalPages(meta.totalProductQuestions, this.page.size)

        this.productQuestionsData = data
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToGetProductQuestions)
      }

      this.isProductQuestionsDataLoading = false
    },
    async updateProductQuestion (productQuestionId: string, authScope: AuthScope): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isLoading = true

      try {
        await $apiHelper.productQuestions.updateProductQuestion(
          productQuestionId,
          {
            answer: this.form.answer,
            question: this.form.question,
            statusId: this.form.status
          },
          authScope
        )
        const response = await $apiHelper.productQuestions.getProductQuestion(productQuestionId, authScope) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.productQuestionData = data

        const questionIndex = this.productQuestionsData.findIndex(
          (x: ProductQuestionData) => x.id === data.attributes.id
        )

        this.productQuestionsData[questionIndex] = data

        $toast.success(messages.productQuestionIsSaved)

        this.productQuestionToForm()
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToUpdateProductQuestion)
      }

      this.hasUnsavedChanges = false
      this.isLoading = false
    }
  }
})
